/**
 * ContactTriggerSSR.js
 * --------------------
 * Server-side rendering (SSR) stub implementation of the ContactTrigger system.
 * This class provides a no-operation (no-op) version of the ContactTrigger interface
 * to ensure compatibility during SSR. It prevents DOM or browser API access and
 * safely defers all interactive behavior to the client-side version.
 *
 * Purpose:
 * - Avoid runtime errors when the contact system is initialized in a Node.js (SSR) environment
 * - Provide a unified interface for both SSR and browser code paths
 * - Return safely structured debug metadata during SSR hydration diagnostics
 * - Track SSR usage patterns for debugging client-side hydration issues
 *
 * Responsibilities:
 * - Expose no-op versions of all public-facing ContactTrigger methods
 * - Log activity using DebugHelper for observability
 * - Return false or undefined values as appropriate to indicate inactivity
 * - Prevent global state registration or DOM manipulation
 * - Track method calls to identify potential hydration mismatches
 *
 * Key Methods (all are no-op in SSR):
 * - `init()`, `setupTriggers()`, `processContactClick()`, `handleFallback()`
 * - `createFallbackForm()`, `attemptRedirect()`, `triggerContact()`, `destroy()`
 * - `getDebugInfo()`: returns SSR-aware metadata for diagnostics
 * - `getUsageStats()`: returns method call statistics for debugging
 * - `static getGlobalDebugInfo()`: returns global SSR diagnostics
 * - `static forceCleanup()`: no-op placeholder for SSR environment
 *
 * Limitations:
 * - No UI or DOM behavior is provided — this class is inert by design
 * - Does not support hydration reattachment; client-side must handle full initialization
 * - Intended for environments where `window` and `document` are not defined
 * - Usage tracking is basic and only helpful for debugging hydration issues
 */

import DebugHelper from '../utils/DebugHelper.js';

export default class ContactTriggerSSR {
    constructor(namespace) {
        this.namespace = namespace;
        this.initialized = false;
        this.createdAt = new Date().toISOString();
        
        // Track method calls for debugging hydration issues
        this.methodCalls = {
            init: 0,
            setupTriggers: 0,
            processContactClick: 0,
            handleFallback: 0,
            createFallbackForm: 0,
            attemptRedirect: 0,
            triggerContact: 0,
            destroy: 0
        };
        
        DebugHelper.info('Running in SSR mode (no-op)', 'ContactTriggerSSR');
    }

    /**
     * Track method call for debugging
     * @param {string} methodName Name of the called method
     */
    _trackCall(methodName) {
        if (this.methodCalls.hasOwnProperty(methodName)) {
            this.methodCalls[methodName]++;
        }
    }

    /**
     * Initialize the SSR stub (no-op)
     * @returns {ContactTriggerSSR} This instance for chaining
     */
    init() {
        this._trackCall('init');
        DebugHelper.info('SSR init (no-op)', 'ContactTriggerSSR');
        return this;
    }

    /**
     * Setup triggers (no-op in SSR)
     */
    setupTriggers() {
        this._trackCall('setupTriggers');
        DebugHelper.info('SSR setupTriggers (no-op)', 'ContactTriggerSSR');
    }

    /**
     * Process contact click (no-op in SSR)
     * @param {*} button Button element (unused in SSR)
     */
    processContactClick(button) {
        this._trackCall('processContactClick');
        DebugHelper.info('SSR processContactClick (no-op)', 'ContactTriggerSSR');
        
        // Log if this gets called during SSR - might indicate a hydration issue
        if (this.methodCalls.processContactClick === 1) {
            DebugHelper.warn('processContactClick called during SSR - check for premature hydration', 'ContactTriggerSSR');
        }
    }

    /**
     * Handle fallback (no-op in SSR)
     */
    handleFallback() {
        this._trackCall('handleFallback');
        DebugHelper.info('SSR handleFallback (no-op)', 'ContactTriggerSSR');
        
        // Log if fallback is attempted during SSR
        if (this.methodCalls.handleFallback === 1) {
            DebugHelper.warn('handleFallback called during SSR - check contact system initialization', 'ContactTriggerSSR');
        }
    }

    /**
     * Create fallback form (no-op in SSR)
     * @returns {boolean} Always returns false in SSR
     */
    createFallbackForm() {
        this._trackCall('createFallbackForm');
        DebugHelper.info('SSR createFallbackForm (no-op)', 'ContactTriggerSSR');
        return false;
    }

    /**
     * Attempt redirect (no-op in SSR)
     */
    attemptRedirect() {
        this._trackCall('attemptRedirect');
        DebugHelper.info('SSR attemptRedirect (no-op)', 'ContactTriggerSSR');
    }

    /**
     * Destroy instance (no-op in SSR)
     */
    destroy() {
        this._trackCall('destroy');
        DebugHelper.info('SSR destroy (no-op)', 'ContactTriggerSSR');
    }

    /**
     * Trigger contact manually (no-op in SSR)
     */
    triggerContact() {
        this._trackCall('triggerContact');
        DebugHelper.info('SSR triggerContact (no-op)', 'ContactTriggerSSR');
        
        // Log if manual trigger is called during SSR
        if (this.methodCalls.triggerContact === 1) {
            DebugHelper.warn('triggerContact called during SSR - check for client-side code running on server', 'ContactTriggerSSR');
        }
    }

    /**
     * Get initialization status (always false in SSR)
     * @returns {boolean} Always false in SSR
     */
    isInitialized() {
        return false;
    }

    /**
     * Get usage statistics for debugging
     * @returns {Object} Usage statistics
     */
    getUsageStats() {
        return {
            createdAt: this.createdAt,
            totalMethodCalls: Object.values(this.methodCalls).reduce((sum, count) => sum + count, 0),
            methodCalls: { ...this.methodCalls },
            mostCalledMethod: Object.entries(this.methodCalls).reduce((a, b) => a[1] > b[1] ? a : b)[0],
            hasNamespace: !!this.namespace
        };
    }

    /**
     * Get debug information for SSR instance
     * @returns {Object} Debug info object
     */
    getDebugInfo() {
        return {
            ssrMode: true,
            initialized: false,
            globalInstance: false,
            globalListenerActive: false,
            globalListenerProcessing: false,
            usageStats: this.getUsageStats()
        };
    }

    /**
     * Get recent errors (no-op for SSR, maintains API compatibility)
     * @returns {Array} Always empty array in SSR
     */
    getRecentErrors() {
        return [];
    }

    /**
     * Check for critical issues (no-op for SSR, maintains API compatibility)
     * @returns {boolean} Always false in SSR
     */
    hasCriticalIssues() {
        return false;
    }

    /**
     * Clear error history (no-op for SSR, maintains API compatibility)
     */
    clearErrorHistory() {
        // No-op in SSR
    }

    /**
     * Get full status (enhanced for SSR debugging)
     * @returns {Object} Full status information
     */
    getFullStatus() {
        return {
            ssrMode: true,
            initialized: false,
            isGlobalInstance: false,
            namespace: {
                hasNamespace: !!this.namespace,
                namespaceType: this.namespace ? typeof this.namespace : null
            },
            usageStats: this.getUsageStats(),
            debug: this.getDebugInfo(),
            timestamp: new Date().toISOString()
        };
    }

    /**
     * Get global debug information for SSR
     * @returns {Object} Global debug info object
     */
    static getGlobalDebugInfo() {
        return {
            ssrMode: true,
            hasGlobalInstance: false,
            globalListenerState: {
                active: false,
                processing: false
            },
            environment: 'SSR'
        };
    }

    /**
     * Force cleanup (no-op in SSR)
     */
    static forceCleanup() {
        DebugHelper.info('SSR forceCleanup (no-op)', 'ContactTriggerSSR');
    }

    /**
     * Check if global instance exists (always false in SSR)
     * @returns {boolean} Always false in SSR
     */
    static hasGlobalInstance() {
        return false;
    }

    /**
     * Get global instance (always null in SSR)
     * @returns {null} Always null in SSR
     */
    static getGlobalInstance() {
        return null;
    }
}
